import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MOBILE_BREAKPOINT } from 'constants/Styles.constants'
import { ChannelTypeEnum, Identity, Maybe } from '@vendorflow/types'
import { EndpointAndTimestamp } from 'pages/WorkspacePage/Workspace.types'
import { MessageBodyTypeEnum } from '@vendorflow/types/lib/types/Messages'
import { getDisplayNameFromIdentity } from 'services/Identity.service'
import MobileDetect from 'mobile-detect'
import { convertFromStandardToDisplay } from 'converters/Phone.converter'
import { useEvent } from 'hooks/Utils.hooks'
import { useLocation } from 'react-router-dom'
import { ROUTES } from 'constants/Route.constants'
import { useFirebaseContext } from 'features/firebase/FirebaseProvider'
import {
  getAliceChannelMediumPath,
  getAliceLatestIncomingTimestamps,
  getAliceMediumTracker,
} from 'features/firebase/Firebase.services'
import { MediumsEnum } from 'features/firebase/Firebase.constants'
import { SwitchboardEndpointsEnum } from 'features/switchboard/Switchboard.types'
import { useMyOrgId } from 'queries/Auth.queries'
import { normalizeToEndpointEnum } from 'features/tenants-endpoints/TenantsEndpoints.services'
import { UseDataResult } from 'features/firebase/Firebase.types'

export function useIsInternalChannelView() {
  const location = useLocation()
  const envy = useMyOrgId()!
  return location.pathname.includes(ROUTES.WORKSPACE_INTERNAL_CHANNELS(envy))
}

export function useIsExternalChannelView() {
  const location = useLocation()
  const envy = useMyOrgId()!
  return location.pathname.includes(ROUTES.WORKSPACE_EXTERNAL_CHANNELS(envy))
}

export function checkIsOnMobileOrTabletDevice() {
  const md = new MobileDetect(window.navigator.userAgent)
  return Boolean(md.phone() || md.mobile() || md.tablet())
}

export function useIsOnMobile() {
  const [isOnMobile, setIsOnMobile] = useState<boolean>(window.innerWidth < MOBILE_BREAKPOINT)
  const handleResize = useCallback(() => {
    if (!isOnMobile && window.innerWidth < MOBILE_BREAKPOINT) {
      setIsOnMobile(true)
    } else if (isOnMobile && window.innerWidth >= MOBILE_BREAKPOINT) {
      setIsOnMobile(false)
    }
  }, [isOnMobile, setIsOnMobile])

  useEffect(() => {
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [isOnMobile])

  return isOnMobile
}

export function useScreenOrientationListener(callback: (event: any) => void) {
  useEffect(() => {
    if (screen.orientation) {
      screen.orientation.addEventListener('change', callback)
    } else {
      window.addEventListener('orientationchange', callback)
    }

    return () => {
      if (screen.orientation) {
        screen.orientation.removeEventListener('change', callback)
      } else {
        window.removeEventListener('orientationchange', callback)
      }
    }
  }, [])
}

export function useAppHeight() {
  const [appHeight, setAppHeight] = useState<number>(window.innerHeight)
  const handleResize = useEvent(() => {
    if (window.innerHeight != appHeight) {
      setAppHeight(window.innerHeight)
    }
  })

  useScreenOrientationListener(handleResize)

  useEffect(() => {
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [appHeight])

  return appHeight
}

export function useShowAllFocus() {
  const sixthItemRef = useRef<HTMLLIElement>(null)
  const [isEnterPress, setIsEnterPress] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const intervalCallback = () => {
    if (sixthItemRef.current) {
      // @ts-ignore
      sixthItemRef.current.firstElementChild?.focus()
      setIsFocused(true)
    }
  }
  const interval = useRef<NodeJS.Timeout | null>(null)
  useEffect(() => {
    if (isEnterPress) {
      interval.current = setInterval(intervalCallback, 100)
    }

    return () => {
      if (interval.current) {
        clearInterval(interval.current)
        interval.current = null
      }
    }
  }, [isEnterPress])

  useEffect(() => {
    if (interval.current && isFocused) {
      clearInterval(interval.current)
      interval.current = null
    }
  }, [isFocused])

  return { sixthItemRef, setIsEnterPress }
}

export function getTypeWithoutVersion(bodyType: MessageBodyTypeEnum): string {
  return bodyType.replace(/@\d+.\d+/, '')
}

function getDisplayNameListText(identities: Identity[]): string {
  return identities.reduce<string>((acc, identity: Identity, index: number) => {
    const displayName = getDisplayNameFromIdentity(identity)
    if (identities.length < 3) {
      acc += `${index === 1 ? ' and ' : ''}${displayName}`
    } else {
      if (index < identities.length - 2) {
        acc += `${displayName}, `
      } else if (index === identities.length - 2) {
        acc += `${displayName}, and `
      } else {
        acc += displayName
      }
    }
    return acc
  }, '')
}

function getAddOrRemoveActionText(
  action: 'left' | 'joined',
  isPlural: boolean,
  channelType: ChannelTypeEnum | null
): string {
  const tenseEstablisher =
    action === 'joined' && channelType === ChannelTypeEnum.CAPTIVE_THREAD
      ? isPlural
        ? 'are'
        : 'is'
      : isPlural
      ? 'have'
      : 'has'
  const verb =
    channelType === ChannelTypeEnum.CAPTIVE_THREAD ? (action === 'joined' ? 'watching' : 'stopped watching') : action
  const obj = channelType === ChannelTypeEnum.CAPTIVE_THREAD ? 'conversation' : 'channel'

  return `${tenseEstablisher} ${verb} the ${obj}.`
}

export function getAddRemoveMemberMessage(
  identities: Identity[],
  action: 'left' | 'joined',
  channelType: ChannelTypeEnum | null
) {
  const isPlural = identities.length > 1
  const subjects = getDisplayNameListText(identities)
  const statement = getAddOrRemoveActionText(action, isPlural, channelType)

  return `${subjects} ${statement}`
}

export function useLastReceivedMessageCommMedium(aliceId: Maybe<string>): {
  data: EndpointAndTimestamp
  dataUpdatedAt: number | null
} {
  const { useFirebaseData } = useFirebaseContext()
  const incoming = useFirebaseData<{ [key: string]: { timestamp: number } }>(getAliceLatestIncomingTimestamps(aliceId))
  const result = useMemo(() => {
    let medium: SwitchboardEndpointsEnum = SwitchboardEndpointsEnum.SMS
    let timestamp = 0
    Object.entries(incoming.data || {}).forEach(([key, value]) => {
      if (value?.timestamp && value.timestamp > timestamp) {
        medium = normalizeToEndpointEnum(key as MediumsEnum | SwitchboardEndpointsEnum)!
        timestamp = value.timestamp
      }
    })

    return { medium, timestamp }
  }, [incoming.dataUpdatedAt])

  return { data: result, dataUpdatedAt: incoming.dataUpdatedAt }
}

/**
 * Used to make sure we are actually waiting until new RTDB data has loaded for this alice before triggering
 * the alice medium auto updater logic.
 */
function useAliceRtdbDataLoadedResolver(
  alice: Maybe<string>,
  lastReceived: UseDataResult<EndpointAndTimestamp>,
  tracker: UseDataResult<EndpointAndTimestamp | null>
) {
  const loadState = useRef<{ alice: Maybe<string>; lastReceived: boolean; tracker: boolean }>({
    alice: null,
    lastReceived: false,
    tracker: false,
  })
  // if the alice has changed, then we need to reset our load state
  useEffect(() => {
    if (alice) {
      loadState.current = { alice, lastReceived: false, tracker: false }
    }
  }, [alice])
  useEffect(() => {
    if (alice && lastReceived.dataUpdatedAt) {
      loadState.current.lastReceived = true
    }
  }, [lastReceived.dataUpdatedAt])
  useEffect(() => {
    if (alice && tracker.dataUpdatedAt) {
      loadState.current.tracker = true
    }
  }, [tracker.dataUpdatedAt])

  return loadState.current
}

export function useAliceMediumAutoUpdater(alice: Maybe<string>, channel: Maybe<string>) {
  const { useFirebaseData, setFirebaseData } = useFirebaseContext()
  const lastReceived = useLastReceivedMessageCommMedium(alice)
  const tracker = useFirebaseData<EndpointAndTimestamp>(getAliceMediumTracker(alice))
  const loadState = useAliceRtdbDataLoadedResolver(alice, lastReceived, tracker)

  useEffect(() => {
    async function performUpdates(alice: string, channel: string, lastReceived: EndpointAndTimestamp) {
      await setFirebaseData<EndpointAndTimestamp>(getAliceMediumTracker(alice), lastReceived)
      await setFirebaseData<Maybe<SwitchboardEndpointsEnum>>(
        getAliceChannelMediumPath(channel),
        normalizeToEndpointEnum(lastReceived.medium)
      )
    }

    if (
      alice &&
      channel &&
      lastReceived.dataUpdatedAt &&
      tracker.dataUpdatedAt &&
      loadState.lastReceived &&
      loadState.tracker &&
      lastReceived.data.medium !== tracker.data?.medium &&
      lastReceived.data.timestamp > (tracker.data?.timestamp != null ? tracker.data?.timestamp : -1)
    ) {
      void performUpdates(alice, channel, lastReceived.data)
    }
  }, [lastReceived.dataUpdatedAt, tracker.dataUpdatedAt])
}

export function filterIdentities(identities: Identity[] | undefined, search: string) {
  const searchText = search.toLowerCase().trim()
  return (identities || []).filter(
    ({ firstName, lastName, displayName, emails, phones }) =>
      (displayName != null && displayName.toLowerCase().includes(searchText)) ||
      (firstName != null && firstName.toLowerCase().includes(searchText)) ||
      (lastName != null && lastName.toLowerCase().includes(searchText)) ||
      emails.some((email) => email.toLowerCase().includes(searchText)) ||
      phones.some(
        (phone) => phone.number.includes(searchText) || convertFromStandardToDisplay(phone.number).includes(searchText)
      )
  )
}
